import { Asset, District } from 'components/common/types/Asset.types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { formatCurrency, formatDate, formatNumber, formatPercent } from 'modules/I18N';
import {
  AllocationStatus,
  BookedCampaignData,
  BookedCampaigns,
  BookedCampaignsAllocation,
  BookedCampaignsResponse,
} from 'components/common/types/BookedCampaigns';
import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import { AssetItem, FurnitureItem } from './useFurniture.types';
import { FrameOccupancyStatus, FrameStatusChip, UseBookedCampaignsResult } from './useBookedCampaigns.types';
import { FRAME_STATUS_CHIPS, FULL_SOT, SOT_EPSILON } from './consts';

export const toTitleCase = (str: string): string => {
  return str.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export const formatFurnitureItem = (
  furnitureCode: string,
  {
    longitude,
    latitude,
    address,
    postcode,
    district,
  }: { longitude: number; latitude: number; address?: string; postcode?: string; district: District },
): Omit<FurnitureItem, 'assetsInformation'> => {
  return {
    furnitureCode,
    longitude,
    latitude,
    address1: district ? toTitleCase(`${address}, ${district.districtName}, ${postcode}`) : '',
    address2: district ? toTitleCase(`${district.town.townName}, ${district.town.county.countyName}`) : '',
  };
};

export const formatAssetsInformation = ({
  asset,
  localeCode,
  currencyCode,
  routeFrameCodes,
  shouldConvertPercentage,
}: {
  asset: Asset;
  localeCode: string;
  currencyCode: string;
  routeFrameCodes: RouteFrameCode[];
  shouldConvertPercentage: boolean;
}): AssetItem => {
  const mediaOwnerData = asset.mediaOwnerAssetId || { mediaOwner: asset.mediaOwner || '', assetId: asset.frameId };

  return {
    mediaOwnerAssetId: mediaOwnerData || undefined,
    frameId: asset.routeFrameCode,
    sot: formatPercent({ value: shouldConvertPercentage ? asset.sot : asset.sot / 100, localeCode }),
    impressions: formatNumber({ value: asset.impressions, localeCode }),
    price: formatCurrency({ value: asset.price ?? 0, currencyCode, localeCode }),
    isSelected: Boolean(
      routeFrameCodes?.find((frameCode) => frameCode.code === asset.routeFrameCode && frameCode.include),
    ),
    isMissing: asset.availability === 'Missing',
    isDigital: asset.productFormat?.digital,
    isSot: Boolean(asset.sot),
    isImpressions: Boolean(asset.impressions),
    isAvailable: asset.availability === 'Available',
    isPrice: Boolean(asset.price),
    routeFrameCode: asset.routeFrameCode,
    productFormatName: asset.productFormat?.productFormatName,
    frameSize: asset.frameSizeName,
  };
};

export const formatBookedCampaigns = ({
  allocations,
  startDate,
  endDate,
  localeCode,
}: {
  allocations: BookedCampaignsAllocation[];
  startDate: string | Date;
  endDate: string | Date;
  localeCode: string;
}): BookedCampaignData[] => {
  return allocations.map((allocation) => ({
    id: allocation.campaignId,
    title: allocation.campaignName,
    startDate: formatDate(startDate, localeCode),
    endDate: formatDate(endDate, localeCode),
    sot: allocation.sot * 100,
    status: allocation.status as DirectSalesStatusOption,
    agency: allocation.agency ?? '',
    salesPerson: allocation.salesPerson ?? '',
    adminPerson: allocation.adminPerson ?? '',
    advertiser: allocation.advertiser ?? '',
    brand: allocation.brand ?? '',
    bookedImpressions: allocation.bookedImpressions,
    allocationStatus: allocation.allocationStatus as AllocationStatus,
    campaignName: allocation.campaignName,
    campaignId: allocation.campaignId,
    dealLineId: allocation.dealLineId,
  }));
};

const sumSot = (allocations: BookedCampaignsAllocation[]): number =>
  allocations.reduce((sum, allocation) => sum + allocation.sot, 0);

const isFullSot = (totalSot: number): boolean => totalSot >= FULL_SOT - SOT_EPSILON;

const hasBookedSot = (totalSot: number): boolean => totalSot > SOT_EPSILON;

export const getFrameStatusChip = ({
  isAvailable,
  isFrameAllocated,
  isDirectSalesCampaignType,
  frameOccupancyStatus,
}: {
  isAvailable: boolean;
  isFrameAllocated: boolean;
  isDirectSalesCampaignType: boolean;
  frameOccupancyStatus?: FrameOccupancyStatus;
}): FrameStatusChip | null => {
  if (isFrameAllocated) return FRAME_STATUS_CHIPS.allocated;
  if (isAvailable) return null;
  if (!isDirectSalesCampaignType) return FRAME_STATUS_CHIPS.booked;

  return frameOccupancyStatus ? FRAME_STATUS_CHIPS[frameOccupancyStatus] : null;
};

export const formatFrameOccupancy = ({
  frameIds,
  frames,
  startDate,
  endDate,
  localeCode,
}: {
  frameIds: string[];
  frames: BookedCampaignsResponse['frames'];
  startDate: string | Date;
  endDate: string | Date;
  localeCode: string;
}): UseBookedCampaignsResult => {
  const requestedFrameIds = new Set(frameIds);
  const occupancyStatuses: Record<string, FrameOccupancyStatus> = {};
  frameIds.forEach((frameId) => {
    occupancyStatuses[frameId] = 'notAllocated';
  });

  const bookedCampaigns = frames.reduce((acc: BookedCampaigns, frame) => {
    if (!requestedFrameIds.has(frame.frameId)) return acc;

    const totalSot = sumSot(frame.allocations);
    occupancyStatuses[frame.frameId] = isFullSot(totalSot) ? 'booked' : 'notAllocated';

    if (hasBookedSot(totalSot)) {
      acc[frame.frameId] = formatBookedCampaigns({
        allocations: frame.allocations,
        startDate,
        endDate,
        localeCode,
      });
    }

    return acc;
  }, {});

  return { bookedCampaigns, occupancyStatuses };
};
